import { useNewBillingRoutesAvailable } from 'app/hooks/useNewBillingRoutesAvailable';
import { useEntitlements } from 'app/modules/settings/modules/billing/hooks/useEntitlements';
import {
  isNewBillingGovernedEngine,
  resolveBillingEngine,
} from 'app/modules/settings/modules/billing/types/entitlements';

/**
 * Whether the current account is governed by new-billing (v2).
 *
 * Two signals, both from the api:
 *
 * 1. `GET /desktopapp/billing/subscription` — a 404 means the NewBilling routes
 *    are off for this account (see {@link useNewBillingRoutesAvailable}).
 * 2. `GET /desktopapp/billing/entitlements` → `billingEngine`. The routes being
 *    on is not enough: with `NEW_BILLING_ENABLED_FOR_ALL` every legacy account
 *    gets them too, and treating those as migrated drops the legacy expiry
 *    guard and caps (audit 1.5). So once the api says which engine governs the
 *    account, only `'newbilling'` counts; `'legacy'` and `'frozen'` do not.
 *    When the field is absent (an api without it) the routes signal decides,
 *    as before.
 *
 * We default to `true` (v2) while loading or on any non-404 outcome, and return
 * `false` once the subscription query has positively resolved as a 404 or the
 * entitlements name a non-NewBilling engine. Defaulting to v2 is
 * self-correcting: a genuinely non-migrated account briefly routed to v2 hits
 * the v2 page's existing 404 -> v1 bounce, whereas a migrated account wrongly
 * routed to v1 has no correcting redirect. The subscription query is not
 * reliably warm on the trial-expired path, so we must not gate on `isSuccess`
 * (which is false while loading and would send migrated users to v1 — BUG-13).
 */
export function useIsNewBillingEnabled(): boolean {
  const routesAvailable = useNewBillingRoutesAvailable();
  const entitlementsQuery = useEntitlements();

  if (!routesAvailable) return false;

  return isNewBillingGovernedEngine(
    resolveBillingEngine(entitlementsQuery.data),
  );
}
